import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TestTube, SignOut } from "@phosphor-icons/react";

const STATUS_COLORS = {
  pending: "bg-terracotta text-white",
  under_review: "bg-slate text-white",
  reviewed: "bg-green-700 text-white",
  clarification_required: "bg-yellow-600 text-white",
};

export default function LabAdminPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [queue, setQueue] = useState([]);
  const [active, setActive] = useState(null);
  const [status, setStatus] = useState("reviewed");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/lab-reports/queue").then((r) => setQueue(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("status", status);
      fd.append("notes", notes);
      await api.put(`/lab-reports/${active.id}/review`, fd);
      toast.success("Review saved");
      setActive(null); setNotes("");
      load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-bone flex">
      <aside className="hidden md:flex w-60 flex-col bg-charcoal text-bone">
        <div className="p-6 border-b border-white/10">
          <div className="font-clinical text-xl">Gutvibe</div>
          <div className="overline text-terracotta text-[10px] mt-1">Lab Review</div>
        </div>
        <nav className="p-3 flex-1 space-y-1">
          <Link to="/lab" className="block px-4 py-2 rounded-md bg-white/10 text-sm">Review queue</Link>
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-sm mb-2">{user?.name}</div>
          <Button size="sm" onClick={() => { logout(); nav("/"); }} className="w-full bg-white/10 hover:bg-white/20 text-white border-0" data-testid="lab-logout">
            <SignOut size={14} className="mr-1" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 p-6 md:p-10">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <div className="overline text-terracotta">Diagnostics review</div>
            <h1 className="font-clinical text-4xl text-charcoal mt-1">Review queue</h1>
          </div>
          <Badge className="bg-ayurveda text-bone">{queue.length} pending</Badge>
        </div>

        {queue.length === 0 && (
          <div className="rounded-md bg-white p-10 text-center border border-black/5">
            <TestTube size={40} weight="duotone" className="mx-auto text-ayurveda/40 mb-3" />
            <div className="text-charcoal/60">Queue is empty. Nice work.</div>
          </div>
        )}

        <div className="space-y-2">
          {queue.map((r) => (
            <Card key={r.id} className="border-black/5 bg-white rounded-md" data-testid={`lab-item-${r.id}`}>
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={STATUS_COLORS[r.review_status]}>{r.review_status.replace("_", " ")}</Badge>
                    <span className="text-xs text-charcoal/60">{new Date(r.created_at).toLocaleString("en-IN")}</span>
                  </div>
                  <div className="font-clinical text-charcoal truncate">{r.title}</div>
                  <div className="text-xs text-charcoal/60">Patient: {r.patient_name} · Lab: {r.lab_name || "—"}</div>
                  {r.ai_summary && (
                    <div className="mt-2 rounded bg-bone p-2 text-xs text-charcoal/70 border border-black/5">
                      <div className="overline text-terracotta text-[9px] mb-0.5">AI extract (pre-review)</div>
                      {r.ai_summary.slice(0, 220)}{r.ai_summary.length > 220 && "…"}
                    </div>
                  )}
                </div>
                <Button size="sm" className="bg-ayurveda text-bone" onClick={() => { setActive(r); setNotes(r.reviewer_notes || ""); }} data-testid={`lab-review-${r.id}`}>
                  Review
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
          <DialogContent className="bg-white max-w-lg">
            <DialogHeader><DialogTitle className="font-clinical text-2xl">Review: {active?.title}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <div className="overline text-charcoal/50 text-[10px] mb-1">New status</div>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="lab-review-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under_review">Under review</SelectItem>
                    <SelectItem value="reviewed">Reviewed</SelectItem>
                    <SelectItem value="clarification_required">Clarification required</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="overline text-charcoal/50 text-[10px] mb-1">Reviewer notes</div>
                <Textarea rows={6} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Findings, recommended follow-up…" data-testid="lab-review-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setActive(null)}>Cancel</Button>
              <Button onClick={submit} disabled={saving} className="bg-ayurveda text-bone" data-testid="lab-review-save">{saving ? "Saving…" : "Save review"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
